import { MessageSquare, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { getNormalizedStatus } from "../../lib/pedidoConstants";
import { PedidoDetailHeader } from "./Header";
import { PedidoClienteEndereco } from "./ClienteEndereco";
import { PedidoMesaInfo } from "./MesaInfo";
import { PedidoTimeline } from "./Timeline";
import { PedidoAvaliacao } from "./Avaliacao";
import { PedidoItensSection } from "./ItensSection";
import { PedidoTotaisFooter } from "./TotaisFooter";

/**
 * Modal completo de detalhes do pedido — extraído de Pedidos.js pra ser reusado
 * tanto na tela de Vendas → Pedidos quanto no Relatório de Avaliações.
 *
 * Props (5, agrupadas):
 *  - open / pedido / onClose: estado de visibilidade e dados
 *  - catalog: { productsMap?, dimensions?, moduloBadge? }
 *      productsMap: usado pra resolver fotos dos itens (fallback flat se omitido)
 *      dimensions: config da campanha — ordena ratings da avaliação inline
 *      moduloBadge: nó React opcional (badge do módulo com ícone) — o caller
 *        renderiza porque os ícones de módulo (iFood, Grandchef, etc) ficam em
 *        Pedidos.js. Em Avaliações pode ser null.
 *  - actions: { onReprint?, onRequestCancel?, extraFooterButtons? }
 *      Quando ausentes, os botões correspondentes não renderizam (read-only).
 */
export function PedidoDetailDialog({ open, pedido, onClose, catalog = {}, actions = {} }) {
  const { productsMap = {}, dimensions = [], moduloBadge = null } = catalog;
  const { onReprint = null, onRequestCancel = null, extraFooterButtons = null } = actions;

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose?.() : null)}>
      <DialogContent
        className="max-w-[100vw] sm:max-w-[95vw] md:max-w-lg w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col m-0 sm:m-4 rounded-none sm:rounded-lg p-0"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Detalhes do Pedido</DialogTitle>
        {pedido && (
          <>
            <PedidoDetailHeader pedido={pedido} moduloBadge={moduloBadge} onReprint={onReprint} />
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              <PedidoClienteEndereco pedido={pedido} />
              <PedidoMesaInfo pedido={pedido} />
              <PedidoTimeline pedido={pedido} />
              <PedidoAvaliacao pedido={pedido} dimensions={dimensions} />
              <PedidoItensSection items={pedido.items || []} productsMap={productsMap} />
              {(pedido.observacao || pedido.observacoes) && (
                <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded-lg p-2.5 border border-yellow-200 dark:border-yellow-800">
                  <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 flex items-center gap-1 mb-0.5">
                    <MessageSquare className="w-3.5 h-3.5" /> Observação
                  </p>
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {pedido.observacao || pedido.observacoes}
                  </p>
                </div>
              )}
            </div>
            <PedidoTotaisFooter pedido={pedido} />
            <ActionsRow
              pedido={pedido}
              onRequestCancel={onRequestCancel}
              extraFooterButtons={extraFooterButtons}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ActionsRow({ pedido, onRequestCancel, extraFooterButtons }) {
  const isCancel = getNormalizedStatus(pedido.status) === "cancelado";
  const showCancel = !!onRequestCancel && !isCancel;
  if (!showCancel && !extraFooterButtons) return null;
  return (
    <div className="border-t px-4 py-3 shrink-0 flex flex-wrap gap-2">
      {showCancel && (
        <Button
          variant="destructive"
          size="sm"
          className="flex-1 h-9 text-xs"
          onClick={() => onRequestCancel(pedido)}
        >
          <XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar Pedido
        </Button>
      )}
      {extraFooterButtons}
    </div>
  );
}
